import { ApiCall } from '../helper/axios';
const BASE = '/api/report-builder';
const reportBuilderService = {
    listReports(params = {}, token) {
        return ApiCall('GET', `${BASE}/reports`, params, {
            Authorization: token || '',
        });
    },
    /**
     * The report as a CSV file (a Blob). Same body as getNetworkReport — a
     * `report_id` alone exports the saved report.
     */
    exportReport(body, token) {
        return ApiCall('POST', `${BASE}/export`, body, { Authorization: token || '' }, 7 * 60 * 1000, 'blob');
    },
    /** Delete a saved report. */
    deleteReport(reportId, token) {
        return ApiCall('DELETE', `${BASE}/reports/${encodeURIComponent(reportId)}`, '', {
            Authorization: token || '',
        });
    },
    /** Rename a saved report — 409 (`field: "report_name"`) when the client already has that name. */
    renameReport(reportId, reportName, token) {
        return ApiCall('PATCH', `${BASE}/reports/${encodeURIComponent(reportId)}/name`, { report_name: reportName }, { Authorization: token || '' });
    },
    /**
     * Copy a saved report to a client under a new name — 409 (`field:
     * "report_name"`) when that client already has the name. Answers with the
     * copy's `report_id`.
     */
    cloneReport(reportId, body, token) {
        return ApiCall('POST', `${BASE}/reports/${encodeURIComponent(reportId)}/clone`, body, {
            Authorization: token || '',
        });
    },
    /**
     * Email a saved report to its client's active, opted-in contacts - 400
     * when there are none. Answers with the `recipients` it went to.
     */
    sendReport(reportId, token) {
        return ApiCall('POST', `${BASE}/reports/${encodeURIComponent(reportId)}/send`, {}, {
            Authorization: token || '',
        });
    },
    /**
     * The Refresh button: starts the background re-pull of the report's networks
     * and answers at once. `success: false` with "already running" - one is going.
     */
    startRefresh(reportId, body, token) {
        return ApiCall('POST', `${BASE}/reports/${encodeURIComponent(reportId)}/refresh`, body, {
            Authorization: token || '',
        });
    },
    /** The report's refresh column: `status` working | completed | partial | failed | null, `is_syncing`. */
    getRefreshStatus(reportId, token) {
        return ApiCall('GET', `${BASE}/reports/${encodeURIComponent(reportId)}/refresh-status`, '', {
            Authorization: token || '',
        });
    },
    /** The finished outcome has been shown - remove it so it is not shown again. */
    clearRefreshStatus(reportId, token) {
        return ApiCall('DELETE', `${BASE}/reports/${encodeURIComponent(reportId)}/refresh-status`, '', {
            Authorization: token || '',
        });
    },
    listReportCreators(token) {
        return ApiCall('GET', `${BASE}/reports/creators`, '', {
            Authorization: token || '',
        });
    },
    /** The report's schedule config, or `data: null` when it was never scheduled. */
    getReportSchedule(reportId, token) {
        return ApiCall('GET', `${BASE}/reports/${encodeURIComponent(reportId)}/schedule`, '', {
            Authorization: token || '',
        });
    },
    /** Create or replace the report's schedule - 400 with `field` on a bad value. */
    saveReportSchedule(reportId, body, token) {
        return ApiCall('PUT', `${BASE}/reports/${encodeURIComponent(reportId)}/schedule`, body, {
            Authorization: token || '',
        });
    },
    /** Remove the report's schedule - 404 when it was never scheduled. */
    deleteReportSchedule(reportId, token) {
        return ApiCall('DELETE', `${BASE}/reports/${encodeURIComponent(reportId)}/schedule`, '', {
            Authorization: token || '',
        });
    },
    /** Active contacts of the report's client - [{ contact_id, name, email }]. */
    listReportContacts(reportId, token) {
        return ApiCall('GET', `${BASE}/reports/${encodeURIComponent(reportId)}/contacts`, '', {
            Authorization: token || '',
        });
    },
    /**
     * Report data. With `{ report_id }` alone it opens a saved report: the
     * response adds `saved_report` (the stored document, report_payload and
     * custom_metrics_payload included) to refill the builder.
     */
    getNetworkReport(body, token) {
        return ApiCall('POST', `${BASE}/network-report`, body, {
            Authorization: token || '',
        });
    },
    /**
     * Store a report — upsert on (client_id, report_name). `report_payload` is
     * the network-report body; custom metrics travel inside it and are also
     * sent as `custom_metrics_payload`, which is where the API stores them.
     */
    saveReport(body, token) {
        return ApiCall('POST', `${BASE}/save-report`, body, {
            Authorization: token || '',
        });
    },
    /**
     * Add (no key) or update (existing key) one custom metric on a saved
     * report, found by (client_id, report_name). Answers with the refreshed
     * report `data` and the stored `custom_metrics`.
     */
    saveCustomMetric(body, token) {
        return ApiCall('POST', `${BASE}/custom-metric`, body, {
            Authorization: token || '',
        });
    },
    /** Remove one custom metric; refused while another one depends on it. */
    deleteCustomMetric(body, token) {
        return ApiCall('DELETE', `${BASE}/custom-metric`, body, {
            Authorization: token || '',
        });
    },
    /** Networks the client can report on — the API does not filter by dimension. */
    getClientConfig(params, token) {
        return ApiCall('GET', `${BASE}/client-config`, { client_id: params.client_id }, { Authorization: token || '' });
    },
    /** List notes of a report. Filterable by type: 'internal' | 'external'. */
    listReportNotes(reportId, token, type) {
        const query = type ? { type } : undefined;
        return ApiCall('GET', `${BASE}/reports/${reportId}/notes`, query, {
            Authorization: token || '',
        });
    },
    /** Add a new note to a report. */
    addReportNote(reportId, body, token) {
        return ApiCall('POST', `${BASE}/reports/${reportId}/notes`, body, {
            Authorization: token || '',
        });
    },
    /** Update an existing note. */
    updateReportNote(reportId, noteId, body, token) {
        return ApiCall('PUT', `${BASE}/reports/${reportId}/notes/${noteId}`, body, {
            Authorization: token || '',
        });
    },
    /** Delete a note from a report. */
    deleteReportNote(reportId, noteId, token) {
        return ApiCall('DELETE', `${BASE}/reports/${reportId}/notes/${noteId}`, {}, {
            Authorization: token || '',
        });
    },
};
export default reportBuilderService;
